import React, { useEffect, useMemo } from "react";
import { Col, Row, FormGroup, Label, Input, FormText } from "reactstrap";

import ReleaseStoreSection from "../../components/releaseStoreSection";

const YOUTUBE_DECLARATION_DISABLED_MANDATORY_INTERNAL_IDS = [3, 10, 11, 14];
const YOUTUBE_CONTENT_ID_ELIGIBLE_DISABLED_MANDATORY_INTERNAL_IDS = [2];
const STORE_AVAILABILITY_MESSAGE = "Beatport is only available for Dance releases with Beatport-approved labels. Traxsource is only available for Dance releases with Traxsource-approved labels.";
const YOUTUBE_DECLARATION_MESSAGE = "Please sign the YouTube Content ID Declaration first.";
const YOUTUBE_INELIGIBLE_MESSAGE = "This store is ineligible as you've stated that you don't have the ownership rights. If claimed falsely, you could be liable for a legal dispute";
const YOUTUBE_CONTENT_ID_MESSAGE = "This store is not applicable for YouTube Content ID eligible releases because it is delivered to both Youtube Content ID and Youtube Premium.";
const BEATPORT_MESSAGE = "Beatport can be selected only when the Genre is Dance and the label is approved for Beatport.";
const TRAXSOURCE_MESSAGE = "Traxsource can be selected only when the Genre is Dance and the label is approved for Traxsource.";
const JUNO_MESSAGE = "JunoDownload can be selected only when the Genre is Dance.";
const OPTIONAL_ADDITIONAL_NONE_SELECTED_MESSAGE =
  "You haven't selected any Optional Additional Stores. These can expand your reach, Are you sure you want to skip them?";

const noop = () => {};
export const normalizeStoreId = (storeId) => (
  storeId === null || storeId === undefined ? null : String(storeId)
);
const toNormalizedIdSet = (values = [], getId = (value) => value) => new Set(
  values.map((value) => normalizeStoreId(getId(value))).filter((id) => id !== null),
);
const extractStoreId = (store) => {
  if (store === null || store === undefined) {
    return null;
  }

  if (typeof store !== "object") {
    return normalizeStoreId(store);
  }

  if (store.store && typeof store.store === "object") {
    return normalizeStoreId(store.store.id);
  }

  if (store.ddex_store && typeof store.ddex_store === "object") {
    return normalizeStoreId(store.ddex_store.id);
  }

  return normalizeStoreId(
    store.store_id ??
    store.store ??
    store.ddex_store_id ??
    store.ddex_store ??
    store.id
  );
};

const getStoreLabel = (store) => store.display_name || store.name || "";

const getUniqueStores = (stores) => {
  const seen = new Set();

  return stores.filter((store) => {
    const storeId = normalizeStoreId(store?.id);

    if (!store || storeId === null || seen.has(storeId)) {
      return false;
    }

    seen.add(storeId);
    return true;
  });
};

const expandStoreEntries = (stores) =>
  stores.flatMap((store) => {
    const labels = getStoreLabel(store).split(",").map((label) => label.trim()).filter(Boolean);

    return labels.map((label, index) => ({
      ...store,
      checkboxId: `${store.id}-${index}`,
      checkboxLabel: label,
    }));
  });

export const prepareDdexStores = (stores = []) =>
  getUniqueStores(
    stores
      .filter((store) => getStoreLabel(store))
      .sort((a, b) => getStoreLabel(a).localeCompare(getStoreLabel(b)))
  );

export const getSelectedStoreIds = (selectedOptionalStores = [], selectedMandatoryStores = []) =>
  Array.from(
    new Set([...selectedOptionalStores, ...selectedMandatoryStores].map((store) => store?.id))
  ).filter((storeId) => storeId !== null && storeId !== undefined);

export const getStoreIdsFromSource = (stores = []) =>
  stores
    .map((store) => extractStoreId(store))
    .filter((storeId) => storeId !== null && storeId !== undefined);

export const useReleaseStoresView = ({
  ddexStores,
  selectedStoreIds,
  storeSearchTerm = "",
}) => {
  const selectedStoreIdSet = useMemo(() => toNormalizedIdSet(selectedStoreIds), [selectedStoreIds]);
  const storeSearchMatcher = storeSearchTerm.trim().toLowerCase();

  const selectedMandatoryStores = useMemo(() => ddexStores.filter((store) =>
    store.category === "mandatory" && selectedStoreIdSet.has(normalizeStoreId(store.id))
  ), [ddexStores, selectedStoreIdSet]);

  const selectedOptionalStores = useMemo(() => ddexStores.filter((store) =>
    store.category === "optional" && selectedStoreIdSet.has(normalizeStoreId(store.id))
  ), [ddexStores, selectedStoreIdSet]);

  const selectedOptionalPaidStores = useMemo(() => ddexStores.filter((store) =>
    store.category === "optional_paid" && selectedStoreIdSet.has(normalizeStoreId(store.id))
  ), [ddexStores, selectedStoreIdSet]);

  const filteredMandatoryStores = useMemo(() => expandStoreEntries(selectedMandatoryStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [selectedMandatoryStores, storeSearchMatcher]);

  const filteredOptionalStores = useMemo(() => expandStoreEntries(selectedOptionalStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [selectedOptionalStores, storeSearchMatcher]);

  const filteredOptionalPaidStores = useMemo(() => expandStoreEntries(selectedOptionalPaidStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [selectedOptionalPaidStores, storeSearchMatcher]);

  return {
    selectedMandatoryStores,
    selectedOptionalStores,
    selectedOptionalPaidStores,
    filteredMandatoryStores,
    filteredOptionalStores,
    filteredOptionalPaidStores,
  };
};

export const useReleaseStores = ({
  ddexStores,
  storeSearchTerm,
  youtubeDeclarationE,
  youtubeDeclarationNE,
  isDanceGenre,
  isBeatportApproved,
  isTraxsourceApproved,
  selectedGenreLabel,
  isPremiumOrEmployee,
  hasPaidSLAPlan,
  setSelectedOptionalStores,
  /** When true, optional + unlocked optional_paid stores with no disabledReason are selected by default (and when they become selectable). Update flow should pass false to keep API-saved selection. */
  autoSelectPassingOptionalStores = true,
}) => {
  const hasYoutubeDeclarationChoice = youtubeDeclarationE || youtubeDeclarationNE;

  const mandatoryStores = useMemo(() => ddexStores.filter((store) => {
    if (store.category !== "mandatory") {
      return false;
    }
    return true;
  }).map((store) => ({
    ...store,
    disabledReason: !hasYoutubeDeclarationChoice
      ? YOUTUBE_DECLARATION_MESSAGE
      : !youtubeDeclarationE && YOUTUBE_DECLARATION_DISABLED_MANDATORY_INTERNAL_IDS.includes(Number(store.internal_id))
        ? YOUTUBE_INELIGIBLE_MESSAGE
        : youtubeDeclarationE && YOUTUBE_CONTENT_ID_ELIGIBLE_DISABLED_MANDATORY_INTERNAL_IDS.includes(Number(store.internal_id))
          ? YOUTUBE_CONTENT_ID_MESSAGE
          : null,
  })), [ddexStores, youtubeDeclarationE, hasYoutubeDeclarationChoice]);

  const optionalStores = useMemo(() => ddexStores.filter((store) => {
    if (store.category !== "optional") {
      return false;
    }
    return true;
  }).map((store) => {
    let disabledReason = null;

    if (store.name === "Beatport" && (!isDanceGenre || !isBeatportApproved)) {
      disabledReason = BEATPORT_MESSAGE;
    } else if (store.name === "Traxsource" && (!isDanceGenre || !isTraxsourceApproved)) {
      disabledReason = TRAXSOURCE_MESSAGE;
    } else if (store.name === "Junodownload" && selectedGenreLabel !== "Dance") {
      disabledReason = JUNO_MESSAGE;
    }

    return {
      ...store,
      disabledReason,
    };
  }), [ddexStores, isBeatportApproved, isDanceGenre, isTraxsourceApproved, selectedGenreLabel]);

  const optionalPaidStores = useMemo(() => ddexStores.filter((store) => store.category === "optional_paid").map((store) => ({
    ...store,
    disabledReason: hasPaidSLAPlan ? null : (isPremiumOrEmployee ? (
      <>
        Please subscribe to a <a href="/sla" onClick={(e) => e.stopPropagation()}>Paid SLA Plan</a> to unlock the Optional Additional Stores.
      </>
    ) : "Please contact our support team to unlock those stores."),
  })), [ddexStores, isPremiumOrEmployee, hasPaidSLAPlan]);

  const selectedMandatoryStores = useMemo(() => mandatoryStores.filter((store) => !store.disabledReason), [mandatoryStores]);
  const selectableOptionalPaidStores = useMemo(
    () => optionalPaidStores.filter((store) => !store.disabledReason),
    [optionalPaidStores],
  );
  /** Regular optional only — used for default auto-select; optional_paid is never pre-selected. */
  const selectableRegularOptionalStores = useMemo(
    () => optionalStores.filter((store) => !store.disabledReason),
    [optionalStores],
  );
  const selectableOptionalStores = useMemo(() => ([
    ...selectableRegularOptionalStores,
    ...selectableOptionalPaidStores,
  ]), [selectableRegularOptionalStores, selectableOptionalPaidStores]);
  const storeSearchMatcher = storeSearchTerm.trim().toLowerCase();

  const filteredMandatoryStores = useMemo(() => expandStoreEntries(mandatoryStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [mandatoryStores, storeSearchMatcher]);

  const filteredOptionalStores = useMemo(() => expandStoreEntries(optionalStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [optionalStores, storeSearchMatcher]);

  const filteredOptionalPaidStores = useMemo(() => expandStoreEntries(optionalPaidStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [optionalPaidStores, storeSearchMatcher]);

  useEffect(() => {
    if (!setSelectedOptionalStores) {
      return;
    }

    const availableOptionalIds = toNormalizedIdSet(selectableOptionalStores, (store) => store.id);

    setSelectedOptionalStores((prevStores) => {
      const filteredStores = prevStores.filter((store) =>
        availableOptionalIds.has(normalizeStoreId(store.id)),
      );

      let nextStores = filteredStores;
      if (autoSelectPassingOptionalStores) {
        const selectedIds = toNormalizedIdSet(filteredStores, (store) => store.id);
        const toAdd = selectableRegularOptionalStores.filter((store) =>
          !selectedIds.has(normalizeStoreId(store.id)),
        );
        if (toAdd.length > 0) {
          nextStores = [...filteredStores, ...toAdd];
        }
      }

      if (
        nextStores.length === prevStores.length &&
        nextStores.every((store, index) => store.id === prevStores[index].id)
      ) {
        return prevStores;
      }

      return nextStores;
    });
  }, [autoSelectPassingOptionalStores, selectableOptionalStores, selectableRegularOptionalStores, setSelectedOptionalStores]);

  return {
    optionalStores,
    optionalPaidStores,
    selectableOptionalPaidStores,
    selectableOptionalStores,
    selectedMandatoryStores,
    filteredMandatoryStores,
    filteredOptionalStores,
    filteredOptionalPaidStores,
    showAvailabilityMessage: !isDanceGenre || !isBeatportApproved || !isTraxsourceApproved,
  };
};

const ReleaseStores = ({
  storeSearchTerm,
  onStoreSearchChange,
  filteredMandatoryStores,
  filteredOptionalStores,
  filteredOptionalPaidStores,
  selectedMandatoryStores,
  selectedOptionalStores,
  selectedOptionalPaidStores = [],
  setSelectedOptionalStores,
  loadingDdexStores,
  ddexStoresCount,
  showAvailabilityMessage,
  readOnly = false,
  hideSearch = false,
  selectableOptionalPaidStores = [],
}) => {
  const selectableOptionalPaidIds = toNormalizedIdSet(selectableOptionalPaidStores, (store) => store.id);
  const showOptionalAdditionalNoneSelectedWarning =
    !readOnly &&
    selectableOptionalPaidIds.size > 0 &&
    !selectedOptionalStores.some((store) => selectableOptionalPaidIds.has(normalizeStoreId(store.id)));

  return (
  <Row style={{ marginBottom: "10px" }}>
    <Col>
      {!hideSearch && (
        <FormGroup>
          <Label>Stores</Label>
          <Input
            type="search"
            id="releaseStores"
            name="releaseStores"
            placeholder="Search store..."
            value={storeSearchTerm}
            onChange={onStoreSearchChange}
          />
        </FormGroup>
      )}
      {hideSearch && <Label>Stores</Label>}
      <ReleaseStoreSection
        title="Mandatory"
        stores={filteredMandatoryStores}
        selectedStores={selectedMandatoryStores}
        setSelectedStores={noop}
        readOnly={true}
      />
      <ReleaseStoreSection
        title="Optional"
        stores={filteredOptionalStores}
        selectedStores={selectedOptionalStores}
        setSelectedStores={setSelectedOptionalStores}
        readOnly={readOnly}
      />
      <ReleaseStoreSection
        title="Optional Additional Stores"
        stores={filteredOptionalPaidStores}
        selectedStores={selectedOptionalPaidStores.length > 0 ? selectedOptionalPaidStores : selectedOptionalStores}
        setSelectedStores={setSelectedOptionalStores}
        readOnly={readOnly}
      />
      {showOptionalAdditionalNoneSelectedWarning && (
        <FormGroup>
          <FormText color="danger" style={{ fontWeight: 500 }}>
            {OPTIONAL_ADDITIONAL_NONE_SELECTED_MESSAGE}
          </FormText>
        </FormGroup>
      )}
      <FormGroup>
        {!loadingDdexStores && ddexStoresCount === 0 && (
          <FormText color="muted">
            No stores found.
          </FormText>
        )}
        {showAvailabilityMessage && (
          <FormText color="muted">
            {STORE_AVAILABILITY_MESSAGE}
          </FormText>
        )}
      </FormGroup>
    </Col>
  </Row>
  );
};

export default ReleaseStores;
